import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import type { InvoicePrefSubscriber } from "../../api/invoicePrefs";
import { changedBy, deviceChecks, getDiagnosis, registrationChecks, ONU, type DiagnoseCheck, type SubscriberDiagnosis } from "../../api/subscriberDevice";

const MARK: Record<DiagnoseCheck["level"], { text: string; color: "success" | "error" | "default" }> = {
  ok: { text: "OK", color: "success" },
  problem: { text: "Problem", color: "error" },
  info: { text: "Note", color: "default" },
};

function Checks({ checks }: { checks: DiagnoseCheck[] }) {
  return (
    <Table size="small">
      <TableBody>
        {checks.map((c, i) => (
          <TableRow key={`${c.label}-${i}`}>
            <TableCell sx={{ width: 120, fontWeight: 500 }}>{c.label}</TableCell>
            <TableCell sx={{ width: 90 }}>
              <Chip size="small" label={MARK[c.level].text} color={MARK[c.level].color} variant={c.level === "ok" ? "outlined" : "filled"} />
            </TableCell>
            <TableCell sx={{ wordBreak: "break-word" }}>{c.detail}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** The account's ONU changes, newest first (owner 2026-10-17): technical history, no payment or plan. */
function OnuHistory({ d, mySub }: { d: SubscriberDiagnosis; mySub: string | undefined }) {
  return (
    <Box>
      <Typography variant="subtitle2">ONU history</Typography>
      {d.history_unavailable ? (
        <Typography variant="body2" color="text.secondary">
          ONU change history is not available yet.
        </Typography>
      ) : d.device_changes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No ONU change recorded for this subscriber.
        </Typography>
      ) : (
        <Table size="small" aria-label="ONU history">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {d.device_changes.map((c, i) => (
              <TableRow key={`${c.changed_at}-${i}`}>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{c.changed_at}</TableCell>
                <TableCell>
                  <code>{c.from}</code>
                </TableCell>
                <TableCell>
                  <code>{c.to}</code>
                </TableCell>
                <TableCell sx={{ wordBreak: "break-word" }}>{changedBy(c, mySub)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

/**
 * Users › User diagnose (owner 2026-10-17, was PHP userdiagnose.php): what
 * stops a subscriber connecting, one check per line, per device. Read only.
 * The password is never shown -- only whether it is set and right.
 */
export function SubscriberDiagnoseDialog({ ns, subscriber, onClose }: { ns: string; subscriber: InvoicePrefSubscriber | null; onClose: () => void }) {
  const identity = useIdentity();
  const q = useQuery({
    queryKey: ["diagnose", ns, subscriber?.accountid],
    enabled: Boolean(subscriber && identity.token && ns),
    queryFn: ({ signal }) => getDiagnosis(identity, ns, subscriber?.accountid ?? "", signal),
    retry: false,
    staleTime: 0,
  });
  const who = subscriber ? subscriber.name || subscriber.username || subscriber.accountid : "";
  const d = q.data;
  const problems = d ? registrationChecks(d).concat(...d.devices.map((x) => deviceChecks(x, d.now))).filter((c) => c.level === "problem").length : 0;

  return (
    <Dialog open={subscriber !== null} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Diagnose {who}</DialogTitle>
      <DialogContent>
        {q.isPending && <CircularProgress size={24} />}
        {q.error && <Alert severity="error">{q.error instanceof Error ? q.error.message : String(q.error)}</Alert>}
        {d && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Alert severity={problems ? "warning" : "success"}>
              {problems ? `${problems} problem${problems === 1 ? "" : "s"} found.` : "No problem found."} Checked at {d.now}.
            </Alert>
            <Box>
              <Typography variant="subtitle2">Subscriber{d.registration?.mobile ? ` · ${d.registration.mobile}` : ""}</Typography>
              <Checks checks={registrationChecks(d)} />
            </Box>
            {d.devices.length === 0 && <Alert severity="error">No active device on this account: nothing can sign in.</Alert>}
            {d.devices.map((dev) => (
              <Box key={dev.username}>
                <Typography variant="subtitle2">
                  {dev.kind === ONU ? "ONU" : dev.kind === "primary" ? "Mobile sign-in" : dev.kind || "Device"} · <code>{dev.username}</code>
                </Typography>
                <Checks checks={deviceChecks(dev, d.now)} />
              </Box>
            ))}
            <OnuHistory d={d} mySub={identity.subject ?? undefined} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {d && (
          <Button onClick={() => void q.refetch()} disabled={q.isFetching}>
            Check again
          </Button>
        )}
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
